import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useReservationStore } from "../../../stores/useReservationStore";
import { saveFormSession, clearFormSession } from "../utils/formPersistence";
import { SubmittingButton } from "../PaymentButtons";
import type { FinalizeError } from "./useSubmitReservation";
import type { ReservationFormValues } from "../reservation-page.schema";

interface UseCreatePayHandlerParams {
  handleSubmit: UseFormReturn<ReservationFormValues>["handleSubmit"];
  closePaymentModal: () => void;
  submitReservation: (payload: object) => Promise<FinalizeError | null>;
  buildPayload: (
    values: ReservationFormValues,
    methodName: string,
  ) => object;
  onError: (err: FinalizeError) => Promise<void>;
  beforeSubmit?: (values: ReservationFormValues) => Promise<void>;
  selectedTicketIds?: number[];
  preValidate?: () => boolean;
}

export function useCreatePayHandler({
  handleSubmit,
  closePaymentModal,
  submitReservation,
  buildPayload,
  onError,
  beforeSubmit,
  selectedTicketIds,
  preValidate,
}: UseCreatePayHandlerParams) {
  const [submittingButton, setSubmittingButton] = useState<SubmittingButton>(null);
  const { reservationId, products, ticketPersonData: storeTicketPersonData } =
    useReservationStore();

  const createPayHandler = (button: SubmittingButton, methodName: string) =>
    async (e?: React.BaseSyntheticEvent) => {
      const preValid = !preValidate || preValidate();
      if (!preValid) {
        closePaymentModal();
        return;
      }
      return handleSubmit(async (values) => {
        setSubmittingButton(button);
        closePaymentModal();
        await beforeSubmit?.(values);
        saveFormSession(reservationId, {
          formValues: values,
          ticketPersonData: storeTicketPersonData,
          optionQuantities: Object.fromEntries(
            products.map((p) => [p.id, p.optionQuantities]),
          ),
          ticketOptionQuantities: Object.fromEntries(
            products.map((p) => [p.id, p.ticketOptionQuantities]),
          ),
          selectedTicketIds,
        });
        const err = await submitReservation(
          buildPayload(values, methodName),
        );
        if (err) {
          clearFormSession(reservationId);
          setSubmittingButton(null);
          await onError(err);
        }
      }, () => closePaymentModal())(e);
    };

  return { createPayHandler, submittingButton };
}
